import React from "react";
import {
  Drawer,
  Box,
  Typography,
  Chip,
  IconButton,
  Stack,
  Button,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { STATUS_COLORS, STATUS_LABELS, SERVICE_TYPES, FREQUENCY_TYPES } from "../constants";
import { getTechnicianName } from "../utils";
import { formatServiceDate } from "../../../utils/dateUtils";
import { C, SCROLL } from "../theme";

function DetailRow({ label, value }) {
  return (
    <Box
      sx={{
        py: 1.25,
        borderBottom: `1px solid ${C.border}`,
      }}
    >
      <Typography sx={{ fontSize: 11, fontWeight: 700, color: C.textSoft, textTransform: "uppercase" }}>
        {label}
      </Typography>
      <Typography sx={{ mt: 0.4, fontSize: 14, fontWeight: 600, color: C.text }}>
        {value || "—"}
      </Typography>
    </Box>
  );
}

export default function ServiceDetailPanel({
  open,
  onClose,
  selected,
  technicians,
  onEdit,
  onDelete,
}) {
  if (!selected) return null;

  const statusColor = STATUS_COLORS[selected.status] || C.primary;
  const serviceTypeLabel =
    SERVICE_TYPES.find((t) => t.value === selected.type)?.label || selected.type;
  const frequencyLabel = selected.frequency
    ? FREQUENCY_TYPES.find((t) => t.value === selected.frequency)?.label || selected.frequency
    : "—";

  const techNames = selected.technicians?.length
    ? selected.technicians.map((t) => t?.name || t).join(", ")
    : selected.technician
      ? getTechnicianName(selected, technicians)
      : "Sin asignar";

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 380 },
          borderLeft: `1px solid ${C.border}`,
        },
      }}
    >
      <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
        <Box
          sx={{
            px: 2.5,
            py: 2,
            borderBottom: `1px solid ${C.border}`,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 1,
          }}
        >
          <Box>
            <Typography sx={{ fontSize: 18, fontWeight: 700, color: C.text }}>
              Detalle del servicio
            </Typography>
            <Chip
              label={STATUS_LABELS[selected.status]}
              size="small"
              sx={{
                mt: 1,
                height: 24,
                fontWeight: 700,
                fontSize: 11,
                bgcolor: `${statusColor}18`,
                color: statusColor,
                border: `1px solid ${statusColor}40`,
              }}
            />
          </Box>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Box sx={{ ...SCROLL, flex: 1, overflowY: "auto", px: 2.5, py: 1 }}>
          <DetailRow label="Tipo" value={serviceTypeLabel} />
          <DetailRow label="Cliente" value={selected.client?.name || selected.client} />
          <DetailRow label="Sucursal" value={selected.branch?.name || selected.branch} />
          <DetailRow label="Técnicos" value={techNames} />
          <DetailRow
            label="Fecha"
            value={selected.date ? formatServiceDate(selected.date, "es-ES", {
              day: "2-digit",
              month: "long",
              year: "numeric",
            }) : "—"}
          />
          <DetailRow label="Hora" value={selected.hour} />
          <DetailRow label="Recurrencia" value={frequencyLabel} />
          {selected.notes && <DetailRow label="Notas" value={selected.notes} />}
        </Box>

        <Box sx={{ p: 2, borderTop: `1px solid ${C.border}`, flexShrink: 0 }}>
          <Stack spacing={1}>
            <Button
              variant="contained"
              fullWidth
              startIcon={<EditIcon />}
              onClick={onEdit}
              sx={{
                textTransform: "none",
                bgcolor: C.primary,
                boxShadow: "none",
                "&:hover": { bgcolor: C.primary, boxShadow: "none" },
              }}
            >
              Editar
            </Button>
            <Button
              variant="outlined"
              fullWidth
              startIcon={<DeleteIcon />}
              onClick={onDelete}
              sx={{
                textTransform: "none",
                borderColor: C.danger,
                color: C.danger,
                "&:hover": { borderColor: C.danger, bgcolor: "#fef2f2" },
              }}
            >
              Eliminar
            </Button>
          </Stack>
        </Box>
      </Box>
    </Drawer>
  );
}
